import { hash32, seedFromString, unitFromHash } from '@gardener/core';
import type { GameMode } from './modes';

/**
 * Daily challenges and seasons (ADR-0123): three challenges every day, all year; finishing them wins the day. Between
 * seasons a day won pays `DAILY_SEEDS`. A season, when one runs, is a 30-day pass of 30 tiers on top: a day won is a
 * tier, and behind (fewer tiers than days so far), finishing the day offers a catch-up of 3 + days behind challenges that
 * wins one day back, one a day. The best rewards sit at the end (the season's head at 10, outfit at 20, gun skin at 30),
 * never sold. Everything here is data and pure functions over it: the server keeps each account's `SeasonState` and
 * steps it with a round's `challengeCounts` (store/career.ts), the match uses the same counts for the mid-round
 * callout, and the client shows it. Account economy like `store.ts`, not sim balance: not in `contentHash`.
 */

// --- Days -----------------------------------------------------------------------------------------------------------

export const DAY_MS = 86_400_000;
/** The UTC day (days since 1970-01-01) a time in ms falls on: a season's days and the challenges roll at 00:00 UTC. */
export const dayOf = (ms: number): number => Math.floor(ms / DAY_MS);

/** The UTC day of a `YYYY-MM-DD` date (civil to days, pure: content may not use `Date`). */
export function dayOfDate(iso: string): number {
  const [y = 1970, m = 1, d = 1] = iso.split('-').map(Number);
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  return era * 146097 + yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy - 719468;
}

// --- Challenges -----------------------------------------------------------------------------------------------------

/**
 * What one round tells the challenges about one player: the match's tally (`challengeFacts` in packages/match, live
 * mid-round and final at its end). A new challenge that needs a new fact adds it here and there.
 */
export interface ChallengeFacts {
  mode: GameMode;
  /** Their team won the round (known at its end only). */
  won: boolean;
  kills: number;
  /** Knock-outs with a gun they found (crates, the dead, the ground), not the one they start with. */
  foundKills: number;
  assists: number;
  deaths: number;
  /** HP they dealt to anything. */
  damage: number;
  /** Bosses they landed the last hit on. */
  bosses: number;
  crates: number;
  /** Guns they picked up. */
  guns: number;
  /** Zombie Nights: zombies they and their builds took down, and the nights the run has survived. */
  zombies: number;
  nights: number;
  /**
   * Battle Royale: their team's place (1 the winner; while still in, the teams still in, a place they can only beat),
   * 0 in any other mode.
   */
  place: number;
}

interface ChallengeDef {
  /** What the card says; `{n}` is the goal. */
  text: string;
  goal: number;
  /** How far one round takes it. */
  count: (f: ChallengeFacts) => number;
  /** Must be reached within one round (its best round counts), not summed over the day's rounds. */
  best?: true;
  /** Only this mode can do it: at most one such challenge shows at a time (`draw`). */
  mode?: 'royale' | 'zombies';
  /** How often it is drawn, against the others' (1 when missing). */
  weight?: number;
}

/**
 * The pool (ADR-0123): challenges that change how you play a round, Jetpack Joyride's missions, never "play with a
 * friend". One line each; a key is what an account's slots store, so rename a key only with its stored slots in mind
 * (a key missing from the pool is redrawn, `seasonToday`). Each should take one or two rounds: three of them about
 * 20-30 minutes.
 */
export const CHALLENGES = {
  win: { text: 'Win {n} rounds', goal: 2, count: (f) => (f.won ? 1 : 0) },
  kills: { text: 'Knock out {n} gardeners', goal: 8, count: (f) => f.kills },
  foundKills: { text: 'Knock out {n} with guns you find', goal: 4, count: (f) => f.foundKills },
  assists: { text: 'Get {n} assists', goal: 5, count: (f) => f.assists },
  damage: { text: 'Deal {n} damage', goal: 4000, count: (f) => f.damage },
  crates: { text: 'Break {n} crates', goal: 4, count: (f) => f.crates },
  noGun: {
    text: 'Win without picking up a gun',
    goal: 1,
    best: true,
    count: (f) => (f.won && f.guns === 0 ? 1 : 0),
  },
  untouchable: {
    text: 'Win without being knocked out',
    goal: 1,
    best: true,
    count: (f) => (f.won && f.deaths === 0 ? 1 : 0),
  },
  royaleTop: {
    text: 'Finish top 5 in Battle Royale',
    goal: 1,
    best: true,
    mode: 'royale',
    count: (f) => (f.place > 0 && f.place <= 5 ? 1 : 0),
  },
  royaleKills: {
    text: 'Knock out {n} in Battle Royale',
    goal: 3,
    mode: 'royale',
    count: (f) => (f.mode === 'royale' ? f.kills : 0),
  },
  nights: {
    text: 'Survive night {n} in Zombie Nights',
    goal: 5,
    best: true,
    mode: 'zombies',
    count: (f) => (f.mode === 'zombies' ? f.nights : 0),
  },
  zombies: {
    text: 'Take down {n} zombies',
    goal: 100,
    mode: 'zombies',
    count: (f) => f.zombies,
  },
} as const satisfies Record<string, ChallengeDef>;

export type ChallengeKey = keyof typeof CHALLENGES;
export const CHALLENGE_KEYS = Object.keys(CHALLENGES) as ChallengeKey[];
export const isChallengeKey = (key: string): key is ChallengeKey => Object.hasOwn(CHALLENGES, key);

const def = (key: ChallengeKey): ChallengeDef => CHALLENGES[key];

/** A challenge's card text, its goal filled in. */
export const challengeText = (key: ChallengeKey): string => def(key).text.replace('{n}', String(def(key).goal));

/** The progress a challenge needs (`n` reaches it: done). */
export const challengeGoal = (key: ChallengeKey): number => def(key).goal;

/** How far one round takes every challenge (only those it moves). */
export function challengeCounts(f: ChallengeFacts): Partial<Record<ChallengeKey, number>> {
  const out: Partial<Record<ChallengeKey, number>> = {};
  for (const key of CHALLENGE_KEYS) {
    const n = Math.max(0, Math.floor(def(key).count(f)));
    if (n > 0) out[key] = n;
  }
  return out;
}

/**
 * The server room kinds whose rounds count (ADR-0123: the server saw them played): Battle Royale and Zombie Nights. Not a
 * custom room (anyone fills it with bots), the dev lobby or anything offline.
 */
export const SEASON_KINDS: readonly string[] = ['royale', 'zombies'];
export const countsForSeason = (kind: string | null | undefined): boolean => !!kind && SEASON_KINDS.includes(kind);

// --- Seasons --------------------------------------------------------------------------------------------------------

/**
 * What a tier pays: seeds, or a thing (an `ItemId`, `head:reaper`, the Store never sells: only the pass gives it;
 * spelled out here, as store.ts asks this module what the pass gives).
 */
export type TierReward = { seeds: number } | { item: `${'head' | 'outfit' | 'gun'}:${string}` };

export interface SeasonDef {
  key: string;
  name: string;
  /** Its first day, `YYYY-MM-DD` (UTC). */
  start: string;
  /** Its length in days; it has as many tiers. */
  days: number;
  /** Tier 1's reward first. */
  tiers: readonly TierReward[];
}

/** A season's tiers: seeds everywhere, a bit more every fifth, and its three pieces at 10, 20 and 30. */
const tiers = (set: string): TierReward[] =>
  Array.from({ length: 30 }, (_, i): TierReward => {
    const tier = i + 1;
    if (tier === 10) return { item: `head:${set}` };
    if (tier === 20) return { item: `outfit:${set}` };
    if (tier === 30) return { item: `gun:${set}` };
    return { seeds: tier % 5 === 0 ? 100 : 50 };
  });

/**
 * Every season, in order, never overlapping (a test). A new season is a line here and its set (ADR-0112's three
 * pieces, never priced). Between seasons (`seasonOn` is null) the daily challenges go on alone, paying `DAILY_SEEDS`.
 */
export const SEASONS: readonly SeasonDef[] = [
  { key: 'harvest', name: 'Harvest', start: '2027-01-01', days: 30, tiers: tiers('reaper') },
];

/**
 * What a day won pays between seasons: the day's three take 20-30 minutes, so more than a dozen won rounds (20 each), a
 * set (2,500) in ten days. ponytail: picked alone; the whole economy (wins, dailies, tiers, prices) gets one pass soon.
 */
export const DAILY_SEEDS = 250;

/** The season day `day` falls in, with its first day, or null between seasons. */
export function seasonOn(day: number): (SeasonDef & { first: number }) | null {
  for (const s of SEASONS) {
    const first = dayOfDate(s.start);
    if (day >= first && day < first + s.days) return { ...s, first };
  }
  return null;
}

export const seasonByKey = (key: string): SeasonDef | null => SEASONS.find((s) => s.key === key) ?? null;

/** Every tier's thing, by id: never sold, owned only once won (`owns`). */
const SEASON_ITEMS = new Map<string, { season: SeasonDef; tier: number }>(
  SEASONS.flatMap((season) =>
    season.tiers.flatMap((r, i) => ('item' in r ? [[r.item, { season, tier: i + 1 }] as const] : [])),
  ),
);
/** The season and tier that give `id`, or null for a thing no pass gives. */
export const seasonItem = (id: string): { season: SeasonDef; tier: number } | null => SEASON_ITEMS.get(id) ?? null;

// --- An account's pass ----------------------------------------------------------------------------------------------

/** The challenges on show at a time. */
export const SLOTS = 3;

export interface ChallengeSlot {
  key: ChallengeKey;
  /** Progress: summed over the day's rounds, or a `best` challenge's best round. Done at `challengeGoal`. */
  n: number;
}

/**
 * An account's pass (the server's `Player.season`, the client's `PlayerView.season`): normalized to a day by
 * `seasonToday`, stepped by `stepRound`, `reroll`. Written only over the version it was read at (`v`).
 */
export interface SeasonState {
  /** The season the day is in, '' between seasons (the dailies alone: no tiers, no catch-up). */
  season: string;
  /** Bumped by every write: a write is conditional on the version it was worked out from. */
  v: number;
  /** Days won: tiers reached, 0 to the season's days. */
  tier: number;
  /** The UTC day the rest is about. */
  day: number;
  slots: ChallengeSlot[];
  /** Challenges drawn today: the next draw's index. */
  picks: number;
  /** The day's own challenges done (all `SLOTS`: the day is won). */
  done: number;
  /** The day's free reroll is used. */
  rerolled: boolean;
  /** A catch-up running: its challenges in all (3 + days behind when it began) and those done; its slots refill. */
  catchup: { need: number; done: number } | null;
  /** A day was won back today (one a day). */
  caught: boolean;
}

/** What one draw picks: never a challenge `avoid` lists, nor a second mode-only one beside `others`. */
function draw(
  account: string,
  day: number,
  index: number,
  avoid: ChallengeKey[],
  others: ChallengeKey[],
): ChallengeKey {
  const moded = others.some((k) => def(k).mode);
  const pool = CHALLENGE_KEYS.filter((k) => !avoid.includes(k) && !(moded && def(k).mode));
  const total = pool.reduce((t, k) => t + (def(k).weight ?? 1), 0);
  let r = unitFromHash(hash32(seedFromString(account), day, index)) * total;
  for (const k of pool) {
    r -= def(k).weight ?? 1;
    if (r < 0) return k;
  }
  return pool[pool.length - 1] as ChallengeKey;
}

/** Three fresh slots, drawn from index `picks` on. */
function deal(account: string, day: number, picks: number): ChallengeSlot[] {
  const keys: ChallengeKey[] = [];
  for (let i = 0; i < SLOTS; i++) keys.push(draw(account, day, picks + i, keys, keys));
  return keys.map((key) => ({ key, n: 0 }));
}

const copy = (s: SeasonState): SeasonState => ({
  ...s,
  slots: s.slots.map((x) => ({ ...x })),
  catchup: s.catchup && { ...s.catchup },
});

/** A slot is done. */
export const slotDone = (slot: ChallengeSlot): boolean => slot.n >= challengeGoal(slot.key);

/** The day is won and nothing more counts today (its three done, and no catch-up running). */
export const dayComplete = (s: SeasonState): boolean => s.done >= SLOTS && !s.catchup;

/** Which day of its season `s.day` is (1 its first; 0 between seasons). */
export const seasonDay = (s: SeasonState): number => {
  const season = seasonByKey(s.season);
  return season ? s.day - dayOfDate(season.start) + 1 : 0;
};

/**
 * `state` with each slot whose challenge left the pool drawn anew on its day (the same state when none did): the
 * server's as the client's, so a pass kept from before a challenge went shows what the server will.
 */
export function knownSlots(state: SeasonState, account: string): SeasonState {
  if (state.slots.every((x) => isChallengeKey(x.key))) return state;
  const s = copy(state);
  s.slots.forEach((x, i) => {
    if (isChallengeKey(x.key)) return;
    const others = s.slots.filter((y, j) => j !== i && isChallengeKey(y.key)).map((y) => y.key);
    s.slots[i] = { key: draw(account, s.day, s.picks++, others, others), n: 0 };
  });
  return s;
}

/**
 * `state` as of UTC `day` (a stored state may be from another day or season): the same state on its own day, else the
 * day's three fresh slots (the tier kept within its season; 0 between seasons). A slot whose challenge left the pool is
 * drawn anew (`knownSlots`). `v` stays the stored version (the write's guard).
 */
export function seasonToday(state: SeasonState | null | undefined, account: string, day: number): SeasonState {
  const key = seasonOn(day)?.key ?? '';
  if (state && state.season === key && state.day === day) return knownSlots(state, account);
  return {
    season: key,
    v: state?.v ?? 0,
    tier: key && state?.season === key ? state.tier : 0,
    day,
    slots: deal(account, day, 0),
    picks: SLOTS,
    done: 0,
    rerolled: false,
    catchup: null,
    caught: false,
  };
}

/** What a round did to a pass. */
export interface SeasonStep {
  /** The pass after it (its `v` one up). */
  state: SeasonState;
  /** Challenges it finished. */
  done: ChallengeKey[];
  /**
   * The day it won (the day's three, or a catch-up's last: one a round at most) and what that pays: in a season the
   * tier it reached and that tier's reward, between seasons tier 0 and `DAILY_SEEDS`. Null: it won none.
   */
  won: { tier: number; reward: TierReward } | null;
}

/**
 * A round's `challengeCounts` applied to `state` on UTC `day` (the round's end): each open slot moves; the day's
 * three done win the day (in a season a tier, else `DAILY_SEEDS`) and, behind in a season, start a catch-up of 3 + days
 * behind (fresh slots that refill as they are done; the round's counts don't reach them); the catch-up done wins a day
 * back. Null once the day is won, or when the round moved nothing.
 */
export function stepRound(
  state: SeasonState | null | undefined,
  account: string,
  day: number,
  counts: Partial<Record<ChallengeKey, number>>,
): SeasonStep | null {
  const today = seasonToday(state, account, day);
  const season = seasonOn(day);
  if (dayComplete(today)) return null;
  const s = copy(today);
  const done: ChallengeKey[] = [];
  let won: SeasonStep['won'] = null;
  let moved = false;
  const win = () => {
    if (!season) won = { tier: 0, reward: { seeds: DAILY_SEEDS } };
    else if (s.tier < season.days) won = { tier: ++s.tier, reward: season.tiers[s.tier - 1] as TierReward };
  };
  if (!s.catchup) {
    for (const slot of s.slots) {
      const c = counts[slot.key] ?? 0;
      if (!c || slotDone(slot)) continue;
      const n = def(slot.key).best ? Math.max(slot.n, c) : slot.n + c;
      moved ||= n !== slot.n;
      slot.n = n;
      if (slotDone(slot)) {
        done.push(slot.key);
        s.done++;
      }
    }
    if (s.done >= SLOTS && done.length > 0) {
      win();
      const behind = season ? day - season.first + 1 - s.tier : 0;
      if (behind > 0 && !s.caught) {
        s.catchup = { need: SLOTS + behind, done: 0 };
        s.slots = deal(account, day, s.picks);
        s.picks += SLOTS;
      }
    }
  } else {
    const catchup = s.catchup;
    s.slots = s.slots.map((slot, i) => {
      const c = counts[slot.key] ?? 0;
      if (!c || catchup.done >= catchup.need) return slot;
      const n = def(slot.key).best ? Math.max(slot.n, c) : slot.n + c;
      moved ||= n !== slot.n;
      if (n < challengeGoal(slot.key)) return { ...slot, n };
      done.push(slot.key);
      catchup.done++;
      if (catchup.done >= catchup.need) return { ...slot, n };
      const others = s.slots.filter((_, j) => j !== i).map((x) => x.key);
      return { key: draw(account, day, s.picks++, [...others, slot.key], others), n: 0 };
    });
    if (catchup.done >= catchup.need) {
      win();
      s.catchup = null;
      s.caught = true;
    }
  }
  if (!moved) return null;
  s.v = today.v + 1;
  return { state: s, done, won };
}

/**
 * The day's free reroll of slot `slot` (not done, the day not won): a fresh challenge, none showing, at 0. Null when it
 * can't (used, a done slot or day, no such slot).
 */
export function reroll(
  state: SeasonState | null | undefined,
  account: string,
  day: number,
  slot: number,
): SeasonState | null {
  const today = seasonToday(state, account, day);
  const old = today?.slots[slot];
  if (!old || today.rerolled || slotDone(old) || dayComplete(today)) return null;
  const s = copy(today);
  const others = s.slots.filter((_, j) => j !== slot).map((x) => x.key);
  s.slots[slot] = { key: draw(account, day, s.picks++, [...others, old.key], others), n: 0 };
  s.rerolled = true;
  s.v = today.v + 1;
  return s;
}

/**
 * What a live match watches for (the mid-round "CHALLENGE DONE!" callout): each open slot and what this round alone
 * must count to finish it (the rest of its goal; a `best` challenge its whole goal). Empty once the day is won.
 */
export function openChallenges(s: SeasonState | null): { key: ChallengeKey; need: number }[] {
  if (!s || dayComplete(s)) return [];
  return s.slots
    .filter((x) => !slotDone(x))
    .map((x) => ({ key: x.key, need: def(x.key).best ? def(x.key).goal : def(x.key).goal - x.n }));
}
